import { CoreModalDialogFooter, MMButton } from '@components'
import { LivenessContext } from '@context/LivenessContext'
import { toastSuccess } from '@utils'
import { api, LivenessEventTypes, useLivenessQry } from 'mmfintech-backend-api'
import { GlobalContext, tr } from 'mmfintech-commons'
import { useContext, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import CoreLoader from '../CoreLoader'
import { livenessEventLabel } from './livenessStatus'

import './livenessCheckModal.scss'

export const LivenessCheckModal = () => {
  const { modalHide } = useContext(GlobalContext)
  const { livenessOnSuccess, setLivenessOnSuccess, livenessOnError, setLivenessOnError } = useContext(LivenessContext)
  const dispatch = useDispatch()

  // All orchestration — polling, terminal latch, expiry, auto-open, cancel-on-close —
  // lives in the package. This component is presentation over `view`.
  const { livenessCheckId, eventType, verifyUrl, expired, view, popupBlocked, openVerify, handleClose } =
    useLivenessQry({
      // Hand the terminal `/status` response back to whoever started the action,
      // then clear it so it can't leak into the next check.
      onSuccess: response => {
        livenessOnSuccess?.(response)
        setLivenessOnSuccess(null)
      },
      onError: response => {
        livenessOnError?.(response)
        setLivenessOnError(null)
      },
      onClose: () => {
        // Drop any unfired callback so abandoning a check doesn't leak it.
        setLivenessOnSuccess(null)
        setLivenessOnError(null)
        modalHide()
      }
    })

  // Backstop for phone verification specifically: dismissing this modal on a
  // PHONE_CHANGE success invalidates the profile cache directly, regardless of
  // whether a per-flow setLivenessOnSuccess callback was registered (or survived
  // to this point) — covers every current and future PHONE_CHANGE caller (card
  // issuing, security settings) from this one shared modal, not just callers
  // that remember to register their own callback.
  // handleClose runs first (this modal has no close icon and no click-outside
  // dismissal) so a failure in the cache dispatch can never leave it stuck open.
  const handleDone = () => {
    handleClose()
    if (eventType === LivenessEventTypes.PHONE_CHANGE) {
      dispatch(api.util.invalidateTags(['MerchantProfile']))
    }
  }

  const actionLabel = livenessEventLabel(eventType)

  // Reset the latch if a different check takes over (the modal element can be
  // reused across checks, so component state would otherwise leak).
  const toastedRef = useRef(false)
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset when the check id changes
  useEffect(() => {
    toastedRef.current = false
  }, [livenessCheckId])

  useEffect(() => {
    if (view === 'success' && !toastedRef.current) {
      toastedRef.current = true
      toastSuccess(tr('FRONTEND.LIVENESS.SUCCESS.TOAST', 'Verification completed successfully'))
    }
  }, [view])

  if (!livenessCheckId) {
    return null
  }

  if (view === 'success') {
    return (
      <div className='liveness-modal-wrapper' data-test='liveness-modal-success'>
        <div className='liveness-status liveness-status--success'>
          {tr('FRONTEND.LIVENESS.SUCCESS.TITLE', 'Verification complete')}
        </div>
        <p className='liveness-text'>
          {tr(
            'FRONTEND.LIVENESS.SUCCESS.TEXT',
            'Thanks — your identity was verified and your {{action}} has been confirmed.'
          ).replace('{{action}}', actionLabel)}
        </p>
        <CoreModalDialogFooter>
          <MMButton
            fullWidth
            size='large'
            variant='alternative'
            data-test='liveness-done'
            onClick={handleDone}
            aria-label={tr('FRONTEND.LIVENESS.ARIA.DONE', 'Done, close verification')}
            caption={tr('PORTAL.IBAN_REQUEST.STEP_DONE', 'Done')}
          />
        </CoreModalDialogFooter>
      </div>
    )
  }

  if (view === 'rejected') {
    return (
      <div className='liveness-modal-wrapper' data-test='liveness-modal-rejected'>
        <div className='liveness-status liveness-status--failed'>
          {tr('FRONTEND.LIVENESS.FAILED.TITLE', 'Verification unsuccessful')}
        </div>
        <p className='liveness-text'>
          {tr(
            'FRONTEND.LIVENESS.FAILED.TEXT',
            'We could not verify your identity, so the {{action}} was not completed. You can try again.'
          ).replace('{{action}}', actionLabel)}
        </p>

        <CoreModalDialogFooter>
          <div className='liveness-buttons'>
            {verifyUrl && (
              <MMButton
                fullWidth
                size='large'
                variant='alternative'
                data-test='liveness-retry'
                onClick={openVerify}
                aria-label={tr('FRONTEND.LIVENESS.ARIA.RETRY', 'Retry identity verification on our partner site')}
                caption={tr('FRONTEND.IBAN_ISSUING.STATUS.TRY_AGAIN', 'Try again')}
              />
            )}
            <MMButton
              fullWidth
              size='large'
              variant='tertiary'
              data-test='liveness-close'
              onClick={handleClose}
              aria-label={tr('FRONTEND.LIVENESS.ARIA.CLOSE', 'Close verification')}
              caption={tr('FRONTEND.BUTTONS.CLOSE', 'Close')}
            />
          </div>
        </CoreModalDialogFooter>
      </div>
    )
  }

  // The error view covers two cases:
  //  - the status request is failing (e.g. a 404 before the check is queryable) —
  //    we surface it but keep polling, so it flips to success the moment the user
  //    passes the check on the partner site;
  //  - there is no usable `verifyUrl` (AC5) — we can't open a link, so we show an
  //    explicit "link unavailable" state with a way out instead of a dead button.
  if (view === 'error') {
    return (
      <div className='liveness-modal-wrapper' data-test='liveness-modal-error'>
        <div className='liveness-status liveness-status--failed'>
          {verifyUrl
            ? tr('FRONTEND.LIVENESS.ERROR.TITLE', 'Still waiting for verification')
            : tr('FRONTEND.LIVENESS.ERROR.NO_LINK.TITLE', 'Verification link unavailable')}
        </div>
        <p className='liveness-text'>
          {verifyUrl
            ? tr(
                'FRONTEND.LIVENESS.ERROR.TEXT',
                'We can’t confirm your {{action}} yet. Please make sure you complete the steps on our verification partner’s site — we’ll keep checking.'
              ).replace('{{action}}', actionLabel)
            : tr(
                'FRONTEND.LIVENESS.ERROR.NO_LINK.TEXT',
                'We couldn’t load your verification link. Please close this and start the {{action}} again to receive a new one.'
              ).replace('{{action}}', actionLabel)}
        </p>

        {/* Only show the "waiting" spinner when there is a link to complete —
            without one there is nothing for the user to wait on here. */}
        {verifyUrl && (
          <div className='liveness-waiting'>
            <CoreLoader />
            <span>{tr('FRONTEND.LIVENESS.PENDING.WAITING', 'Waiting for confirmation…')}</span>
          </div>
        )}

        <CoreModalDialogFooter>
          <div className='liveness-buttons'>
            {verifyUrl && (
              <MMButton
                fullWidth
                size='large'
                variant='alternative'
                data-test='liveness-error-open'
                onClick={openVerify}
                aria-label={tr('FRONTEND.LIVENESS.ARIA.OPEN', 'Open identity verification on our partner site')}
                caption={tr('FRONTEND.LIVENESS.BUTTON.OPEN', 'Open verification')}
              />
            )}
            <MMButton
              fullWidth
              size='large'
              variant='tertiary'
              data-test='liveness-error-cancel'
              onClick={handleClose}
              aria-label={tr('FRONTEND.LIVENESS.ARIA.CANCEL', 'Cancel verification')}
              caption={
                verifyUrl
                  ? tr('FRONTEND.BUTTONS.CANCEL', 'Cancel')
                  : tr('FRONTEND.IBAN_ISSUING.STATUS.TRY_AGAIN', 'Try again')
              }
            />
          </div>
        </CoreModalDialogFooter>
      </div>
    )
  }

  // pending (PENDING_USER) — also handles expiry inline (no separate view)
  return (
    <div className='liveness-modal-wrapper' data-test='liveness-modal-pending'>
      {expired ? (
        <p className='liveness-text'>
          {tr(
            'FRONTEND.LIVENESS.EXPIRED',
            'This verification link has expired. Please start the {{action}} again to receive a new one.'
          ).replace('{{action}}', actionLabel)}
        </p>
      ) : (
        <>
          <p className='liveness-text'>
            {tr(
              'FRONTEND.LIVENESS.PENDING.TEXT',
              'To complete your {{action}}, we need to verify it’s really you. Please continue to our verification partner in the tab that just opened.'
            ).replace('{{action}}', actionLabel)}
          </p>
          <p className='liveness-text liveness-text--muted'>
            {tr(
              'FRONTEND.LIVENESS.PENDING.HINT',
              'After you finish on the partner website, come back to this tab — we’ll confirm here once it’s done.'
            )}
          </p>

          {/* Only shown once an automatic open attempt is confirmed blocked —
              tells the user why nothing happened and where to go instead. */}
          {popupBlocked && (
            <p className='liveness-text liveness-text--warning' data-test='liveness-popup-blocked-notice' role='status'>
              {tr(
                'FRONTEND.LIVENESS.PENDING.POPUP_BLOCKED',
                'Your browser blocked the verification tab. Tap "Open verification" below to continue.'
              )}
            </p>
          )}

          {/* The spinner only makes sense while we're still polling — once the
              check expires polling stops, so drop it. */}
          <div className='liveness-waiting'>
            <CoreLoader />
            <span>{tr('FRONTEND.LIVENESS.PENDING.WAITING', 'Waiting for confirmation…')}</span>
          </div>
        </>
      )}

      <CoreModalDialogFooter>
        <div className='liveness-buttons'>
          {/* Guard on `verifyUrl` so a dead "Open" button can never render — a
              live check without a link routes to the error view (AC5); this guard
              only matters for the expired case, where the link may be absent. */}
          {verifyUrl && (
            <MMButton
              fullWidth
              size='large'
              variant='alternative'
              data-test='liveness-open'
              onClick={openVerify}
              aria-label={tr('FRONTEND.LIVENESS.ARIA.OPEN', 'Open identity verification on our partner site')}
              caption={tr('FRONTEND.LIVENESS.BUTTON.OPEN', 'Open verification')}
            />
          )}
          {/* Once the check has expired the verification link is dead — swap the
              passive "Cancel" for "Try again", which closes so the user can
              re-initiate the action and get a fresh check. */}
          {expired ? (
            <MMButton
              fullWidth
              size='large'
              variant='tertiary'
              data-test='liveness-restart'
              onClick={handleClose}
              aria-label={tr('FRONTEND.LIVENESS.ARIA.RESTART', 'Start verification again')}
              caption={tr('FRONTEND.IBAN_ISSUING.STATUS.TRY_AGAIN', 'Try again')}
            />
          ) : (
            <MMButton
              fullWidth
              size='large'
              variant='tertiary'
              data-test='liveness-cancel'
              onClick={handleClose}
              aria-label={tr('FRONTEND.LIVENESS.ARIA.CANCEL', 'Cancel verification')}
              caption={tr('FRONTEND.BUTTONS.CANCEL', 'Cancel')}
            />
          )}
        </div>
      </CoreModalDialogFooter>
    </div>
  )
}
